import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Activity, Users, ShieldAlert, Database } from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, SectionLabel, ErrorState } from '@/acc-ui/primitives/Layout';
import { DataTable, type Column } from '@/acc-ui/primitives/DataTable';
import { Input } from '@/acc-ui/primitives/Input';
import { Button } from '@/acc-ui/primitives/Button';
import { Badge } from '@/acc-ui/primitives/Badge';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { adminApi, type AuditLogEntry } from '@/api/other-apis';
import { usePermission } from '@/auth/use-permission';
import { ADMIN_VIEW_AUDIT_LOG } from '@loanaudit/shared';

/**
 * Admin → System.
 *
 * System info tiles + audit log. Audit log is filtered to the last 90 days
 * by default (full history is retained but scrolling through 7 years is
 * useless). Filters: actor email, action, date range.
 */

export function AdminSystemPage() {
  const { has } = usePermission();
  const canSeeAuditLog = has(ADMIN_VIEW_AUDIT_LOG);

  const sysQuery = useQuery({ queryKey: ['adminSystemInfo'], queryFn: adminApi.systemInfo });

  return (
    <AppShell crumb="Admin" title="System">
      <PageHeader
        title="System Admin"
        subtitle="Platform health, configuration, and activity trail."
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <StatTile
          icon={<Users className="w-4 h-4" />}
          label="Active users"
          value={sysQuery.data?.activeUsers ?? 0}
        />
        <StatTile
          icon={<Activity className="w-4 h-4" />}
          label="Active sessions"
          value={sysQuery.data?.activeSessions ?? 0}
        />
        <StatTile
          icon={<ShieldAlert className="w-4 h-4" />}
          label="Audit entries"
          value={sysQuery.data?.auditLogEntries ?? 0}
        />
        <StatTile
          icon={<Database className="w-4 h-4" />}
          label="Module docs"
          value={sysQuery.data?.docCount ?? 0}
        />
      </div>

      {/* Environment card */}
      {sysQuery.isError ? (
        <ErrorState
          description="Could not load system info."
          action={<Button onClick={() => sysQuery.refetch()}>Retry</Button>}
        />
      ) : (
        <Card padding={false}>
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-glass-hairline">
            <InfoCell label="Environment" value={sysQuery.data?.environment ?? '—'} tone="highlight" />
            <InfoCell label="Node" value={sysQuery.data?.nodeVersion ?? '—'} mono />
            <InfoCell label="Commit" value={sysQuery.data?.gitCommit ?? 'local'} mono />
            <InfoCell label="Service" value={sysQuery.data?.renderService ?? 'local'} mono />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-glass-hairline border-t border-glass-hairline">
            <InfoCell label="Region" value={sysQuery.data?.region ?? '—'} mono />
            <InfoCell label="LLM" value={sysQuery.data?.llmModel ?? '—'} mono />
            <InfoCell label="Provider" value={sysQuery.data?.llmProvider ?? '—'} mono />
            <InfoCell
              label="Uptime"
              value={sysQuery.data ? formatUptime(sysQuery.data.uptimeSeconds) : '—'}
            />
          </div>
        </Card>
      )}

      {/* Audit log */}
      {canSeeAuditLog && <AuditLogSection />}
    </AppShell>
  );
}

function StatTile({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="liquid-glass-strong rounded-glass-lg p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-brand-blue/10 text-brand-blue flex items-center justify-center">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-micro font-semibold uppercase tracking-wide text-text-muted">
          {label}
        </div>
        <div className="text-display font-semibold tabular-nums text-text-primary">
          {value.toLocaleString('en-IN')}
        </div>
      </div>
    </div>
  );
}

function InfoCell({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: 'highlight';
}) {
  return (
    <div className="px-4 py-3 min-w-0">
      <SectionLabel>{label}</SectionLabel>
      <div
        className={
          'mt-1 truncate ' +
          (mono ? 'font-mono text-caption ' : 'text-label ') +
          (tone === 'highlight' ? 'text-emerald-700 font-medium' : 'text-text-primary')
        }
      >
        {value}
      </div>
    </div>
  );
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ---------- Audit log ----------

function defaultFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
}

function AuditLogSection() {
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [actorEmail, setActorEmail] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState<string>(defaultFrom());
  const [to, setTo] = useState<string>('');

  const actionsQuery = useQuery({
    queryKey: ['auditActions'],
    queryFn: adminApi.auditActions,
  });

  const debouncedActorEmail = useDebouncedValue(actorEmail.trim(), 300);
  const logQuery = useQuery({
    queryKey: ['auditLog', { page, pageSize, actorEmail: debouncedActorEmail, action, from, to }],
    queryFn: () =>
      adminApi.auditLog({
        page,
        pageSize,
        actorEmail: debouncedActorEmail || undefined,
        action: action || undefined,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const columns: Column<AuditLogEntry>[] = useMemo(
    () => [
      {
        key: 'createdAt',
        header: 'When',
        cell: (r) => (
          <span className="text-caption text-text-secondary tabular-nums">
            {new Date(r.createdAt).toLocaleString()}
          </span>
        ),
        width: '12rem',
      },
      {
        key: 'actor',
        header: 'Actor',
        cell: (r) => (
          <div className="flex flex-col">
            <span className="text-caption text-text-primary">
              {r.actorEmail ?? '(system)'}
            </span>
          </div>
        ),
      },
      {
        key: 'action',
        header: 'Action',
        cell: (r) => (
          <code className="font-mono text-caption text-brand-blue bg-brand-blue/5 rounded px-1.5 py-0.5">
            {r.action}
          </code>
        ),
        width: '14rem',
      },
      {
        key: 'target',
        header: 'Target',
        cell: (r) => (
          <div className="flex flex-col">
            <Badge tone="muted">{r.targetType}</Badge>
            {r.targetId && (
              <code className="font-mono text-micro text-text-tertiary mt-0.5 truncate">
                {r.targetId}
              </code>
            )}
          </div>
        ),
        width: '14rem',
      },
      {
        key: 'ip',
        header: 'IP',
        cell: (r) => (
          <code className="font-mono text-micro text-text-tertiary">{r.ipAddress ?? '—'}</code>
        ),
        width: '9rem',
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-3">
      <PageHeader title="Audit log" subtitle="Every sensitive action, append-only. Last 90 days by default." />

      <Card padding={false}>
        <div className="p-3 border-b border-glass-hairline grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold text-text-secondary">Actor email</span>
            <Input
              leftIcon={<Search className="w-4 h-4" />}
              placeholder="name@axisfinance.in"
              value={actorEmail}
              onChange={(e) => {
                setActorEmail(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold text-text-secondary">Action</span>
            <select
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setPage(1);
              }}
              className="h-10 px-3 bg-white/70 border border-glass-hairline rounded-glass-sm text-label text-text-primary outline-none focus:border-brand-blue/40"
            >
              <option value="">All actions</option>
              {(actionsQuery.data ?? []).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold text-text-secondary">From</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold text-text-secondary">To</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>
      </Card>

      <DataTable<AuditLogEntry>
        columns={columns}
        rows={logQuery.data?.items ?? []}
        rowKey={(r) => r.id}
        page={page}
        pageSize={pageSize}
        total={logQuery.data?.total ?? 0}
        onPageChange={setPage}
        loading={logQuery.isLoading}
        emptyMessage="No audit entries match the current filters."
      />
    </div>
  );
}
